import React from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	TooltipProps,
	XAxis,
	YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { PROGRAM_DAYS, SOWS_MAX_SCORE } from "../../constants/program";

export interface SowsChartPoint {
	day: number;
	score: number;
}

interface SowsProgressionChartProps {
	title: string;
	data: SowsChartPoint[] | undefined;
	isLoading: boolean;
	color?: string;
	emptyMessage?: string;
}

interface ChartPoint {
	day: number;
	score: number | null;
}

const buildSeries = (data: SowsChartPoint[] | undefined): ChartPoint[] => {
	const byDay = new Map<number, number>();
	(data ?? []).forEach((p) => byDay.set(p.day, p.score));
	return Array.from({ length: PROGRAM_DAYS }, (_, i) => {
		const day = i + 1;
		const score = byDay.get(day);
		return { day, score: score === undefined ? null : score };
	});
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
	active,
	payload,
}) => {
	if (!active || !payload || payload.length === 0) {
		return null;
	}

	const point = payload[0].payload as ChartPoint;
	if (point.score === null) {
		return null;
	}

	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				px: 1.5,
				py: 1,
				boxShadow: 2,
			}}
		>
			<Typography variant="body2">
				Day {point.day}: {point.score.toFixed(1)}
			</Typography>
		</Box>
	);
};

const SowsProgressionChart: React.FC<SowsProgressionChartProps> = ({
	title,
	data,
	isLoading,
	color,
	emptyMessage,
}) => {
	const { t } = useTranslation("dashboard");
	const theme = useTheme();
	// SIMS-7548 AC1 — the design draws this line in the brand tertiary
	// (`--brand-tertiary-main`), which the app carries as the `tertiaryColor` theme
	// role. Unlike the bar-chart series this one IS tenant-branded, so it resolves
	// from the theme rather than the fixed chart palette. An explicit `color` prop
	// still wins, so callers that need a different line keep that option.
	const lineColor = color ?? theme.palette.tertiary.main;

	const series = buildSeries(data);
	const hasAnyData = series.some((p) => p.score !== null);
	const fallbackEmpty = emptyMessage ?? t("sowsNoDataAvailable");

	return (
		<Box
			sx={{
				// SIMS-7548 — the design's chart card: background, 1px border, 12px
				// radius, 24px padding, and NO shadow (`statsTab`'s `card()` helper).
				// The own-margin this card used to carry (`mb: 4`) has moved out to the
				// callers: a component that spaces itself cannot sit in a flex/grid
				// layout without its margin stacking on the container's gap.
				bgcolor: "background.paper",
				border: 1,
				borderColor: "divider",
				borderRadius: (theme) => theme.spacing(1.5),
				p: 3,
			}}
		>
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
				{title}
			</Typography>

			{isLoading ? (
				<Skeleton variant="rectangular" height={280} />
			) : !hasAnyData ? (
				<Box
					sx={{
						height: 280,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="body2" color="textSecondary">
						{fallbackEmpty}
					</Typography>
				</Box>
			) : (
				<ResponsiveContainer width="100%" height={280}>
					<AreaChart
						data={series}
						margin={{ top: 16, right: 24, bottom: 0, left: 0 }}
					>
						<defs>
							<linearGradient
								id={`sowsFill-${lineColor}`}
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop offset="0%" stopColor={lineColor} stopOpacity={0.35} />
								<stop offset="100%" stopColor={lineColor} stopOpacity={0.05} />
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" vertical={false} />
						<XAxis
							dataKey="day"
							type="number"
							domain={[1, PROGRAM_DAYS]}
							ticks={Array.from({ length: PROGRAM_DAYS }, (_, i) => i + 1)}
							allowDecimals={false}
						/>
						<YAxis
							domain={[0, SOWS_MAX_SCORE]}
							ticks={[0, 16, 32, 48, SOWS_MAX_SCORE]}
							allowDecimals={false}
						/>
						<Tooltip content={<CustomTooltip />} />
						<Area
							type="monotone"
							dataKey="score"
							stroke={lineColor}
							strokeWidth={2.5}
							fill={`url(#sowsFill-${lineColor})`}
							connectNulls={false}
							// SIMS-8882 — the design's markers are hollow: a white centre ringed
							// by the accent, not a solid dot. `withdrawalChart()` in the authoring
							// source draws each one at radius 5 (6 while hovered), filled white and
							// stroked in the accent at 2.5 — so the radius change on hover maps onto
							// Recharts' dot/activeDot pair.
							//
							// `background.paper` supplies that white: it is the card surface the
							// chart sits on, and the AC2 guard forbids hex literals in this file
							// (comments included), so the design's value is described, not quoted.
							dot={{
								r: 5,
								fill: theme.palette.background.paper,
								stroke: lineColor,
								strokeWidth: 2.5,
							}}
							activeDot={{
								r: 6,
								fill: theme.palette.background.paper,
								stroke: lineColor,
								strokeWidth: 2.5,
							}}
							isAnimationActive={false}
						/>
					</AreaChart>
				</ResponsiveContainer>
			)}
		</Box>
	);
};

export default SowsProgressionChart;
